import { useEffect, useState, type ReactNode } from 'react'
import { Trash2, X } from 'lucide-react'
import { placeFilled, placeProblem, profileFilled, profileProblem, savePlaceForm, saveProfileForm } from '../lib/catalog'
import type { Place, PlaceForm as Form, Producer, ProducerKind, ProducerRecord, ProfileForm } from '../types'
import { PlaceForm } from './PlaceForm'
import { ProfileFields } from './ProfileFields'
import { KINDS } from './catalogForm'

// A new place or profile without leaving the event (PRD.md, items 4 and 98):
// the same form as Lugares and Perfis. Clicking outside does nothing, and
// closing keeps what was filled with the event, as Gmail minimizes a message,
// until it is created or discarded.

type Created = Pick<ProducerRecord, 'id' | 'name' | 'handle'>

function Dialog({ title, intro, busy, onMinimize, children, actions }: {
  title: string
  intro: string
  busy: boolean
  onMinimize: () => void
  children: ReactNode
  actions: ReactNode
}) {
  useEffect(() => {
    const close = (event: KeyboardEvent) => { if (event.key === 'Escape' && !busy) onMinimize() }
    window.addEventListener('keydown', close)
    return () => window.removeEventListener('keydown', close)
  }, [busy, onMinimize])
  return (
    <div className="dialog-backdrop" role="presentation">
      <section className="dialog dialog--wide quick-create" role="dialog" aria-modal="true" aria-label={title}>
        <div className="dialog__heading">
          <div><h2>{title}</h2><p>{intro}</p></div>
          <button type="button" className="icon-button" aria-label="Fechar" title="Fechar e continuar depois" disabled={busy} onClick={onMinimize}><X size={18} /></button>
        </div>
        <div className="quick-create__body">{children}</div>
        <div className="dialog__actions">{actions}</div>
      </section>
    </div>
  )
}

const discardButton = (busy: boolean, filled: boolean, what: string, onDiscard: () => void) => (
  <button type="button" className="button button--quiet" disabled={busy} onClick={() => {
    if (!filled || window.confirm(`Descartar ${what}? O que foi preenchido se perde.`)) onDiscard()
  }}><Trash2 size={16} /> Descartar</button>
)

export function PlaceQuickCreate({ value, draftId, producers, onChange, onMinimize, onDiscard, onCreated, onError }: {
  value: Form
  // The Lugares draft it continues, closed on creating.
  draftId?: string | null
  producers: Producer[]
  onChange: (value: Form) => void
  onMinimize: () => void
  onDiscard: () => void
  // With the place's profile when it has one.
  onCreated: (place: Place, profile: Created | null) => void
  onError: (message: string) => void
}) {
  const [saving, setSaving] = useState(false)
  const [imagePending, setImagePending] = useState(false)

  const create = async () => {
    const problem = imagePending ? 'A foto ainda não foi preparada: toque em Buscar imagem e espere ficar pronta.' : placeProblem(value)
    if (problem) { onError(problem); return }
    setSaving(true)
    try {
      const { place, profile } = await savePlaceForm(value, { producers, draftId })
      onCreated({ id: place.id, name: place.name, city: place.city, address: place.address }, profile)
    } catch (requestError) {
      onError(requestError instanceof Error ? requestError.message : 'Não foi possível criar o lugar.')
      setSaving(false)
    }
  }

  return (
    <Dialog
      title="Novo lugar"
      intro="Criado ao tocar em Criar lugar, já fica escolhido no evento. Fechar a janela guarda o que foi preenchido, para continuar depois."
      busy={saving}
      onMinimize={onMinimize}
      actions={<>
        {discardButton(saving, placeFilled(value), 'este lugar', onDiscard)}
        <button type="button" className="button button--primary" disabled={saving} onClick={() => void create()}>{saving ? 'Criando…' : 'Criar lugar'}</button>
      </>}
    >
      <PlaceForm compact value={value} onChange={onChange} disabled={saving} producers={producers} onPendingImage={setImagePending} onError={onError} />
    </Dialog>
  )
}

// A producer or an artist for an event; a place's profile comes with the place
// (PRD.md, item 13).
export function ProfileQuickCreate({ value, draftId, onChange, onMinimize, onDiscard, onCreated, onError }: {
  value: ProfileForm
  draftId?: string | null
  onChange: (value: ProfileForm) => void
  onMinimize: () => void
  onDiscard: () => void
  onCreated: (profile: Created) => void
  onError: (message: string) => void
}) {
  const [saving, setSaving] = useState(false)
  const [imagePending, setImagePending] = useState(false)

  const create = async () => {
    const problem = imagePending ? 'A foto ainda não foi preparada: toque em Buscar imagem e espere ficar pronta.' : profileProblem(value)
    if (problem) { onError(problem); return }
    setSaving(true)
    try {
      const producer = await saveProfileForm(value, { draftId })
      onCreated({ id: producer.id, name: producer.name, handle: producer.handle })
    } catch (requestError) {
      onError(requestError instanceof Error ? requestError.message : 'Não foi possível criar o perfil.')
      setSaving(false)
    }
  }

  return (
    <Dialog
      title="Novo perfil"
      intro="Produtor ou artista. Criado ao tocar em Criar perfil, já entra no evento. Fechar a janela guarda o que foi preenchido, para continuar depois."
      busy={saving}
      onMinimize={onMinimize}
      actions={<>
        {discardButton(saving, profileFilled(value), 'este perfil', onDiscard)}
        <button type="button" className="button button--primary" disabled={saving} onClick={() => void create()}>{saving ? 'Criando…' : 'Criar perfil'}</button>
      </>}
    >
      <div className="form-grid">
        <label className="field"><span>Tipo</span><select aria-label="Tipo do novo perfil" disabled={saving} value={value.kind} onChange={(event) => onChange({ ...value, kind: event.target.value as ProducerKind })}>{KINDS.filter((kind) => kind.value !== 'venue').map((kind) => <option key={kind.value} value={kind.value}>{kind.label}</option>)}</select></label>
      </div>
      <ProfileFields value={value} disabled={saving} onChange={(patch) => onChange({ ...value, ...patch })} onPendingImage={setImagePending} onError={onError} />
    </Dialog>
  )
}
